import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import { SendNotification } from "../redux/actions/teams/members/SendNotificationAction";
import { GetNotificationsApi } from "../redux/actions/notifications/GetNotificationsApi";

const Notifications = () => {
  const dispatch = useDispatch();

  // Redux states
  const {
    loading: sendLoading,
    error: sendError,
    response: sendResponse,
  } = useSelector((state) => state.sendNotification);

  const {
    loading: logsLoading,
    error: logsError,
    response: logsResponse,
  } = useSelector((state) => state.NotificationsLogs);

  const [formData, setFormData] = useState({
    title: "",
    message: "",
    target: "all",
  });
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(5);

  // Fetch logs
  useEffect(() => {
    dispatch(GetNotificationsApi("sent", currentPage, limit));
  }, [dispatch, currentPage, limit]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setFormError("");
    setSuccessMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim() || !formData.message.trim()) {
      setFormError("Please fill all required fields (Title & Message).");
      return;
    }

    setFormError("");
    setSuccessMsg("");

    try {
      await dispatch(SendNotification(formData));
      setSuccessMsg("Notification sent successfully!");
      setFormData({ title: "", message: "", target: "all" });

      // Refresh logs
      setTimeout(() => {
        dispatch(GetNotificationsApi("sent", 1, limit));
        setCurrentPage(1);
      }, 1000);
    } catch (error) {
      console.error("Send notification error:", error);
      // setFormError("Something went wrong while sending notification.");
    }
  };

  const handleRetry = () => {
    dispatch(GetNotificationsApi("sent", currentPage, limit));
  };

  const logs = logsResponse?.data?.data || [];
  const totalPages = logsResponse?.data?.totalPages || 1;

  return (
    <div className="content-wrapper" style={{ backgroundColor: "#f5f5f5" }}>
      <section className="main-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-10 col-lg-8">
              {/* Notification Form */}
              <div className="p-4 shadow rounded bg-white mb-5">
                <h5 className="text-center mb-4">Notification Management</h5>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Title</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label>Target Audience</label>
                      <select
                        className="form-select"
                        name="target"
                        value={formData.target}
                        onChange={handleChange}
                      >
                        <option value="all">All Users</option>
                        <option value="Basic">Basic Plan</option>
                        <option value="Monthly Plan">Monthly Plan</option>
                        <option value="Annual Plan">Annual Plan</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label>Message</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Enter message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>

                  {/* Validation / Error / Success Messages */}
                  {formError && (
                    <div className="alert alert-danger py-2">{formError}</div>
                  )}
                  {sendError && (
                    <div className="alert alert-danger py-2">
                      {sendError?.data || "Error while sending notification."}
                    </div>
                  )}
                  {successMsg && (
                    <div className="alert alert-success py-2">{successMsg}</div>
                  )}

                  <div className="text-center">
                    <button
                      type="submit"
                      className="btn btn-success px-4"
                      disabled={sendLoading}
                    >
                      {sendLoading ? (
                        <PulseLoader size={8} color="#fff" />
                      ) : (
                        "Send Now"
                      )}
                    </button>
                  </div>
                </form>
              </div>

              {/* Notification Logs — your original untouched part */}
              <div className="p-4 shadow rounded bg-white">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="m-0">Notification Logs</h5>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() =>
                      dispatch(GetNotificationsApi("sent", currentPage, limit))
                    }
                  >
                    Refresh
                  </button>
                </div>

                {logsLoading ? (
                  <div
                    style={{ height: "30vh" }}
                    className="d-flex align-items-center justify-content-center"
                  >
                    <PulseLoader size={20} color="#49a496" />
                  </div>
                ) : logsError ? (
                  <div className="text-center text-danger py-4">
                    <p>{logsError.data || "Failed to load logs"}</p>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={handleRetry}
                    >
                      Try Again
                    </button>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <p>No notifications sent yet.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle">
                      <thead className="table-light">
                        <tr>
                          <th>Title</th>
                          <th>Message</th>
                          <th>Target</th>
                          <th>Sent At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.map((item) => (
                          <tr key={item._id}>
                            <td>{item.title}</td>
                            <td>{item.message}</td>
                            <td>{item.target}</td>
                            <td>
                              {new Date(item.sentAt).toLocaleString("en-IN", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                      >
                        Previous
                      </button>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {/* End of Notification Logs */}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Notifications;
